import React, {FC, useCallback, useEffect, useState} from 'react';
import {Option, Paper, Select, Alert} from '@deere/fuel-react';
import {useOktaAuth} from '@okta/okta-react';
import {useTranslation} from 'react-i18next';
import {
    AutocompleteValue,
    Box,
    Button,
    // Checkbox, // Uncomment when walk-in student names are needed
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    InputLabel,
    // Stack, // Uncomment when walk-in student names are needed
    // TextField,
    Typography
} from '@mui/material';

interface Student {
    studentId: string;
    firstName: string;
    lastName: string;
    emailId: string;
    displayValue?: string;
    available?: boolean;
}

interface AssignClassAssessmentProps {
    DialogOpen: any;
    handleDialogClose: () => void;
    classId?: string;
    courseId?: string;
}

interface SubmitAssessmentParams {
    state: any;
    setState: any;
    authState: any;
    classId: string | undefined;
    handleConfirmClose: () => void;
    t: (key: string) => string;
}

const SELECT_ALL_VALUE = 'SELECT_ALL';

const formatStudentData = (data: Student[]): Student[] => {
    return data.map((student: Student) => ({
        ...student,
        displayValue: `${student.firstName} ${student.lastName} (${student.studentId})`
    }));
};

const buildAllStudentValues = (students: Student[]): string[] =>
    students
        .filter((student: Student) => student.available) // Only available students
        .map((student: Student) =>
            JSON.stringify({
                studentId: student.studentId,
                emailId: student.emailId,
                firstName: student.firstName,
                lastName: student.lastName
            })
        );

const applySelectAll = (prevState: any): any => {
    const allStudentValues = buildAllStudentValues(
        prevState.availableStudents as Student[]
    );

    return {
        ...prevState,
        selectedStudentIds: [SELECT_ALL_VALUE, ...allStudentValues]
    };
};

const applySelectNone = (prevState: any): any => ({
    ...prevState,
    selectedStudentIds: []
});

const applyIndividualToggle = (
    prevState: any,
    selectedValues: string[]
): any => {
    const filteredValues = selectedValues.filter(
        (v: string) => v !== SELECT_ALL_VALUE
    );
    const allSelected =
    filteredValues.length === (prevState.availableStudents as Student[]).length;
    const newSelected = allSelected ?
        [SELECT_ALL_VALUE, ...filteredValues] :
        filteredValues;

    return {
        ...prevState,
        selectedStudentIds: newSelected
    };
};

const handleStudentFetchSuccess = (data: Student[], setState: any): void => {
    if (!Array.isArray(data)) {
        setState((prev: any) => ({
            ...prev,
            isLoadingStudents: false
        }));

        return;
    }

    const formattedStudents = formatStudentData(data);
    const allStudentValues = buildAllStudentValues(formattedStudents);
    const preSelected =
    formattedStudents.length > 0 ? [SELECT_ALL_VALUE, ...allStudentValues] : [];

    setState((prev: any) => ({
        ...prev,
        availableStudents: formattedStudents,
        selectedStudentIds: preSelected,
        isLoadingStudents: false
    }));
};

const handleStudentFetchError = (error: any, setState: any): void => {
    console.error('Error fetching enrolled students:', error);
    setState((prev: any) => ({
        ...prev,
        isLoadingStudents: false
    }));
};

const handleAssessmentChange = (event: any, setState: any, fetchEnrolledStudents: (classId: string, assessment: string) => void, classId: string | undefined): any => {
    const selectedValue = [...event.currentTarget.selectedOptions].map((option) =>
        option.value
    );

    const parsedAssessment = JSON.parse(selectedValue[0]);
    const assessmentId = parsedAssessment.assessmentId;

    setState((prevState: any) => ({
        ...prevState,
        assessmentName: selectedValue
    }));

    if (classId) {
        fetchEnrolledStudents(classId, assessmentId);
    }
};

// const handleSearchTextChange = ( // Uncomment when walk-in student names are needed
//     e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
//     setState: any
// ): void => {
//     setState((prev: any) => ({
//         ...prev,
//         searchText: e.target.value
//     }));
// };

const handleConfirmNotClose = (setState: any): void => {
    setState((prev: any) => ({
        ...prev,
        isCancelDialogOpen: false,
        activeSubAlert: false
    }));
};

// const handleCheckboxChange = ( // Uncomment when walk-in student names are needed
//     event: React.ChangeEvent<HTMLInputElement>,
//     setState: any
// ): void => {
//     setState((prev: any) => ({
//         ...prev,
//         isCheckboxChecked: event.target.checked
//     }));
// };

// const handleSearchButtonClick = (state: any, setState: any): void => { // Uncomment when walk-in student names are needed
//     setState((prev: any) => ({
//         ...prev,
//         SearchedStud: `Student Name (${state.searchText})`,
//         walkStudIds: [...prev.walkStudIds, state.searchText]
//     // walkStudNames: [...prev.walkStudNames, state.searchText] Uncommnet when walk-in student names are needed
//     }));
// };

const hasUnsavedChanges = (state: any): boolean =>
    state.walkStudNames.length !== 0 || state.assessmentName.length !== 0;

const getStudentPlaceholder = (
    isLoadingStudents: boolean,
    studentCount: number,
    t: (key: string) => string
): string => {
    if (isLoadingStudents) {
        return t('classAssessment.loadingStudents');
    }

    return studentCount === 0 ? t('classAssessment.noStudentsAvailable') : t('classAssessment.selectStudentIds');
};

const getAssessmentPlaceholder = (isLoadingStudents: boolean, t: (key: string) => string): string =>
    isLoadingStudents ? t('classAssessment.loadingAssessment') : t('classAssessment.selectAssessment');

const renderAssessmentOptions = (
    assessmentId: any[],
    isLoadingAssessments: boolean,
    t: (key: string) => string
): JSX.Element[] | JSX.Element | null => {
    if (assessmentId.length > 0) {
        return assessmentId.map((assId) => (
            <Option
                key={assId.assessmentId + assId.assessmentName}
                value={JSON.stringify({
                    assessmentId: assId.assessmentId,
                    assessmentName: assId.assessmentName
                })}
            >
                {`${assId.assessmentId} ${assId.assessmentName}`}
            </Option>
        ));
    }

    if (!isLoadingAssessments) {
        return (
            <Option
                disabled
                value=''
            >
                {t('classAssessment.noAssessmentsAvailable')}
            </Option>
        );
    }

    return null;
};

const getOptionValue = (option: HTMLOptionElement): string => option.value;

function getSelectedValues(target: HTMLSelectElement): string[] {
    return Array.from(target.selectedOptions).map(getOptionValue);
}

function resolveStudentSelection(
    prevState: any,
    selectedValues: string[]
): any {
    const wasSelectAll = prevState.selectedStudentIds.includes(SELECT_ALL_VALUE);
    const isSelectAll = selectedValues.includes(SELECT_ALL_VALUE);
    const selectAllToggled = wasSelectAll !== isSelectAll;

    if (!selectAllToggled) {
        return applyIndividualToggle(prevState, selectedValues);
    }

    return isSelectAll ? applySelectAll(prevState) : applySelectNone(prevState);
}

function handleStudentIdChange(
    event: React.ChangeEvent<{ value: unknown }>,
    setState: any
): void {
    const target = event.target as HTMLSelectElement;
    const selectedValues = getSelectedValues(target);

    setState((prevState: any) =>
        resolveStudentSelection(prevState, selectedValues)
    );
}

const resetState = (setState: any, handleDialogClose: () => void): void => {
    setState((prev: any) => ({
        ...prev,
        isDialogOpen: false,
        isCancelDialogOpen: false,
        isCheckboxChecked: false,
        walkStudNames: [],
        walkStudIds: [],
        searchText: '',
        SearchedStud: '',
        selectedStudentIds: [],
        assessmentName: [],
        activeSubAlert: false,
        isSubmitting: false,
        noAssessmentsForClass: false,
        isAssessmentResponseReceived: false
    }));
    handleDialogClose();
};

const handleCancelWithChanges = (
    state: any,
    setState: any,
    handleConfirmClose: () => void,
    handleDialogClose: () => void
): void => {
    if (hasUnsavedChanges(state)) {
        setState((prev: any) => ({
            ...prev,
            isCancelDialogOpen: true
        }));
    } else {
        handleConfirmClose();
        handleDialogClose();
    }
};

const submitAssessment = (params: SubmitAssessmentParams): void => {
    const {
        state,
        setState,
        authState,
        classId,
        handleConfirmClose,
        t
    } = params;

    const realStudentIds = state.selectedStudentIds.filter(
        (v: string) => v !== SELECT_ALL_VALUE
    );

    if (state.assessmentName.length === 0 || realStudentIds.length === 0) {
        setState((prevState: any) => ({
            ...prevState,
            activeSubAlert: true
        }));
        return;
    }
    setState((prevState: any) => ({
        ...prevState,
        isSubmitting: true
    }));
    const parsedAssessments = state.assessmentName.map((assessment: string) =>
        JSON.parse(assessment)
    );

    fetch(`${process.env.REACT_APP_BACKEND_URL}/assignassessment`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${authState?.accessToken?.accessToken}`,
            'Content-Type': 'application/json',
            idToken: `Bearer ${authState?.idToken?.idToken}`
        },
        body: JSON.stringify({
            assessments: parsedAssessments,
            students: state.selectedStudentIds
                .filter((s: string) => s !== SELECT_ALL_VALUE)
                .map((studentData: string) => JSON.parse(studentData)),
            classId
        })
    })
        .then((resp) => {
            setState((prevState: any) => ({
                ...prevState,
                isSubmitting: false
            }));
            if (!resp.ok) {
                setState((prevState: any) => ({
                    ...prevState,
                    showAlert: true,
                    alertMessage: t('classAssessment.submitError'),
                    alertType: 'error'
                }));
                handleConfirmClose();
                return resp;
            }
            setState((prevState: any) => ({
                ...prevState,
                showAlert: true,
                alertMessage: t('classAssessment.submitSuccess'),
                alertType: 'success'
            }));
            handleConfirmClose();
            return resp;
        })
        .catch((error) => {
            console.error('Error submitting class request:', error);
            setState((prevState: any) => ({
                ...prevState,
                isSubmitting: false,
                showAlert: true,
                alertMessage: t('classAssessment.submitError'),
                alertType: 'error'
            }));
            handleConfirmClose();
            return error;
        });
};

const renderSubmittingSpinner = (isSubmitting: boolean, t: (key: string) => string): JSX.Element | null => {
    if (!isSubmitting) {
        return null;
    }
    return (
        <Box
            sx={{
                marginBottom: '0.5rem'
            }}
        >
            <CircularProgress size={24}/>
            <Typography>{t('common.loading')}</Typography>
        </Box>
    );
};

const isLoaderDialogOpen = (
    dialogOpen: boolean,
    isAssessmentResponseReceived: boolean
): boolean => dialogOpen && !isAssessmentResponseReceived;

const isMainAssessmentDialogOpen = (
    dialogOpen: boolean,
    isAssessmentResponseReceived: boolean,
    noAssessmentsForClass: boolean
): boolean => dialogOpen && isAssessmentResponseReceived && !noAssessmentsForClass;

const isNoAssessmentsDialogOpen = (
    dialogOpen: boolean,
    isAssessmentResponseReceived: boolean,
    noAssessmentsForClass: boolean
): boolean => dialogOpen && isAssessmentResponseReceived && noAssessmentsForClass;

export const ClassAssessment: FC<AssignClassAssessmentProps> = ({
    DialogOpen,
    handleDialogClose,
    classId,
    courseId
}) => {
    const {authState} = useOktaAuth();
    const {t} = useTranslation();
    const [state, setState] = useState({
        anchorEl: null as HTMLElement | null,
        popoverClassId: null as string | null,
        isDialogOpen: false,
        isCheckboxChecked: false,
        walkStudIds: [] as string[],
        walkStudNames: [] as string[],
        searchText: '',
        SearchedStud: '',
        assessmentName: [],
        assessmentId: [] as AutocompleteValue<any, any, any, any>[],
        selectedStudentIds: [] as string[],
        availableStudents: [] as Student[],
        isLoadingStudents: false,
        isLoadingAssessments: false,
        isCancelDialogOpen: false,
        activeSubAlert: false,
        isSubmitting: false,
        noAssessmentsForClass: false,
        isAssessmentResponseReceived: false,
        showAlert: false,
        alertMessage: '',
        alertType: 'success' as 'success' | 'error'
    });

    const fetchAssessments = useCallback(
        (targetedCourseId: string): void => {
            setState((prev) => ({
                ...prev,
                isLoadingAssessments: true,
                isAssessmentResponseReceived: false,
                noAssessmentsForClass: false,
                assessmentId: []
            }));

            fetch(
                `${process.env.REACT_APP_BACKEND_URL}/classassessments?courseId=${targetedCourseId}`,
                {
                    method: 'GET',
                    mode: 'cors',
                    headers: {
                        Authorization: `Bearer ${authState?.accessToken?.accessToken}`,
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        'Access-Control-Allow-Origin': '*',
                        idToken: `Bearer ${authState?.idToken?.idToken}`
                    }
                }
            )
                .then((response) => {
                    return response.json();
                })
                .then((data) => {
                    const hasNoAssessments = !Array.isArray(data) || data.length === 0;

                    setState((prevState) => ({
                        ...prevState,
                        assessmentId: Array.isArray(data) ? data : [],
                        isLoadingAssessments: false,
                        noAssessmentsForClass: hasNoAssessments,
                        isAssessmentResponseReceived: true
                    }));
                    return data;
                })
                .catch((error) => {
                    console.log(error);
                    setState((prevState) => ({
                        ...prevState,
                        isLoadingAssessments: false,
                        noAssessmentsForClass: true,
                        isAssessmentResponseReceived: true
                    }));
                    return error;
                });
        },
        [authState]
    );

    const fetchEnrolledStudents = useCallback(
        (targetClassId: string, assessment: string): void => {
            setState((prev) => ({
                ...prev,
                isLoadingStudents: true,
                availableStudents: [],
                selectedStudentIds: []
            }));

            fetch(
                `${process.env.REACT_APP_BACKEND_URL}/enrolledstudents?scheduleId=${targetClassId}&assessment=${assessment}`,
                {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${authState?.accessToken?.accessToken}`,
                        'Content-Type': 'application/json; charset=UTF-8',
                        Accept: 'application/json'
                    }
                }
            )
                .then((res) => res.json())
                .then((data: Student[]) => {
                    handleStudentFetchSuccess(data, setState);
                    return data;
                })
                .catch((error) => {
                    handleStudentFetchError(error, setState);
                    return error;
                });
        },
        [authState]
    );

    useEffect(() => {
        if (classId && DialogOpen) {
            fetchAssessments(courseId as string);
            // fetchEnrolledStudents(classId, '');
        }
    }, [classId, courseId, DialogOpen, fetchEnrolledStudents, fetchAssessments]);

    // Function to select walk-in students
    // Uncomment when walk-in student names are needed
    // const handlewalkStudChange = (event: React.ChangeEvent<{ value: unknown }>): void => {
    //     const target = event.target as HTMLSelectElement;
    //     const selectedValue = Array.from(target.selectedOptions).map((option) =>
    //         option.value);

    //     setState((prevState: any) => ({
    //         ...prevState,
    //         walkStudNames: selectedValue
    //     }));
    // };

    const handleConfirmClose = (): void => {
        resetState(setState, handleDialogClose);
    };

    const handleDialogCancel = (): void => {
        console.log('Dialog Cancel Clicked', state);
        handleCancelWithChanges(
            state,
            setState,
            handleConfirmClose,
            handleDialogClose
        );
    };

    const handleDialogSubmit = (): void => {
        submitAssessment({
            state,
            setState,
            authState,
            classId,
            handleConfirmClose,
            t
        });
    };

    return (
        <>
            <Dialog open={isMainAssessmentDialogOpen(DialogOpen, state.isAssessmentResponseReceived, state.noAssessmentsForClass)}>
                <DialogTitle>{t('classAssessment.title')}</DialogTitle>
                {state.activeSubAlert && (
                    <Alert
                        title=''
                        variant={'error'}
                    >
                        {t('classAssessment.selectAssessmentAndStudents')}
                    </Alert>
                )}
                <DialogContent>
                    <Box
                        sx={{
                            pb: 10,
                            pl: 20,
                            pr: 20,
                            pt: 10
                        }}
                    >
                        <Paper variant='outlined'>
                            <Grid
                                alignItems='flex-start'
                                container
                                spacing={4}
                            >
                                <Grid
                                    item
                                    xs={12}
                                />
                                <Grid
                                    display={'flex'}
                                    item
                                    xs={4}
                                >
                                    <InputLabel
                                        required
                                        sx={{
                                            textAlign: 'right'
                                        }}
                                    >
                                        {t('classAssessment.assessment')}
                                    </InputLabel>
                                </Grid>
                                <Grid
                                    item
                                    xs={8}
                                >
                                    <Select
                                        disabled={state.isLoadingAssessments}
                                        hasSearch
                                        // multiple
                                        name='roleId'
                                        onChange={(e): void => handleAssessmentChange(e, setState, fetchEnrolledStudents, classId)}
                                        placeholder={getAssessmentPlaceholder(
                                            state.isLoadingAssessments,
                                            t
                                        )}
                                        value={state.assessmentName}
                                    >
                                        {renderAssessmentOptions(
                                            state.assessmentId,
                                            state.isLoadingAssessments,
                                            t
                                        )}
                                    </Select>
                                </Grid>
                                <Grid
                                    display={'flex'}
                                    item
                                    xs={4}
                                >
                                    <InputLabel
                                        required
                                        sx={{
                                            textAlign: 'right'
                                        }}
                                    >
                                        {t('classAssessment.studentId')}
                                    </InputLabel>
                                </Grid>
                                <Grid
                                    item
                                    xs={8}
                                >
                                    <Select
                                        disabled={state.isLoadingStudents}
                                        hasSearch
                                        multiple
                                        name='studentId'
                                        onChange={(e): void => handleStudentIdChange(e, setState)}
                                        placeholder={getStudentPlaceholder(
                                            state.isLoadingStudents,
                                            state.availableStudents.length,
                                            t
                                        )}
                                        textMap={{
                                            selectedMultipleText: (): string =>
                                                t('classAssessment.studentsSelected')
                                        }}
                                        value={state.selectedStudentIds}
                                    >
                                        <Option
                                            key={SELECT_ALL_VALUE}
                                            value={SELECT_ALL_VALUE}
                                        >
                                            <strong>{t('classAssessment.selectAll')}</strong>
                                        </Option>
                                        {state.availableStudents.map((student) => (
                                            <Option
                                                disabled={!student.available}
                                                key={student.studentId}
                                                value={JSON.stringify({
                                                    studentId: student.studentId,
                                                    emailId: student.emailId,
                                                    firstName: student.firstName,
                                                    lastName: student.lastName
                                                })}
                                            >
                                                {student.displayValue}
                                            </Option>
                                        ))}
                                    </Select>
                                    <small
                                        style={{
                                            color: 'grey',
                                            fontSize: '0.75rem'
                                        }}
                                    >
                                        {t('classAssessment.selectAssessmentFirst')}
                                    </small>
                                </Grid>
                                {/* <Grid // Uncomment when walk-in student names are needed
                                    display={'flex'}
                                    item
                                    xs={4}
                                >
                                    <InputLabel
                                        sx={{
                                            textAlign: 'right'
                                        }}
                                    >
                                        {'Walk-in Student'}
                                    </InputLabel>
                                </Grid>
                                <Grid
                                    item
                                    xs={8}
                                >
                                    <Checkbox
                                        checked={state.isCheckboxChecked}
                                        onChange={(e): void => handleCheckboxChange(e, setState)}
                                    />
                                </Grid>
                                <Grid
                                    display={'flex'}
                                    item
                                    xs={4}
                                >
                                    <InputLabel
                                        sx={{
                                            textAlign: 'right'
                                        }}
                                    >
                                        {''}
                                    </InputLabel>
                                </Grid> */}
                                {/* {state.isCheckboxChecked && ( // Uncomment when walk-in student names are needed
                                    <Grid
                                        item
                                        xs={8}
                                    >
                                        {state.SearchedStud}
                                        <Stack
                                            alignItems='center'
                                            direction='row'
                                            spacing={2}
                                        >
                                            <TextField
                                                onChange={(e): void =>
                                                    handleSearchTextChange(e, setState)
                                                }
                                                placeholder='Search'
                                                size='small'
                                                value={state.searchText}
                                                variant='outlined'
                                            />
                                            <Button
                                                onClick={(): void =>
                                                    handleSearchButtonClick(state, setState)
                                                }
                                                style={{
                                                    marginBottom: '3px'
                                                }}
                                                variant={'primary'}
                                            >
                                                {'Search'}
                                            </Button>
                                        </Stack>
                                        <Select
                                            hasSearch
                                            multiple
                                            name='walkStudId'
                                            // onChange={(e): void => handlewalkStudChange(e)}
                                            placeholder='Select Walk-in Student'
                                            value={state.walkStudNames}
                                        >
                                            {state.walkStudIds.map((walkStudId) => (
                                                <Option
                                                    key={walkStudId}
                                                    value={walkStudId}
                                                >
                                                    {walkStudId}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Grid>
                                )} */}
                            </Grid>
                        </Paper>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%'
                        }}
                    >
                        {renderSubmittingSpinner(state.isSubmitting, t)}
                        <Box>
                            <Button
                                onClick={handleDialogCancel}
                                sx={{
                                    marginRight: '1rem'
                                }}
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button
                                disabled={state.isSubmitting}
                                onClick={handleDialogSubmit}
                                variant={'primary'}
                            >
                                {t('common.submit')}
                            </Button>
                        </Box>
                    </Box>
                </DialogActions>
            </Dialog>
            <Dialog
                aria-describedby={'alert-Modal-description'}
                aria-labelledby={'alert-Modal-title'}
                onClose={handleConfirmClose}
                open={state.isCancelDialogOpen}
            >
                <DialogTitle id={'alert-Modal-title'}>
                    {t('classAssessment.confirmCloseTitle')}
                </DialogTitle>
                <DialogActions>
                    <Button
                        onClick={(): void => handleConfirmNotClose(setState)}
                        variant={'secondary'}
                    >
                        {t('classAssessment.no')}
                    </Button>
                    <Button
                        onClick={handleConfirmClose}
                        variant={'primary'}
                    >
                        {t('classAssessment.ok')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={isLoaderDialogOpen(DialogOpen, state.isAssessmentResponseReceived)}>
                <DialogContent>
                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: 2,
                            justifyContent: 'center',
                            py: 4
                        }}
                    >
                        <CircularProgress size={32}/>
                        <Typography>{t('common.loading')}</Typography>
                    </Box>
                </DialogContent>
            </Dialog>
            <Dialog
                aria-describedby={'no-assessments-description'}
                aria-labelledby={'no-assessments-title'}
                onClose={handleConfirmClose}
                open={isNoAssessmentsDialogOpen(DialogOpen, state.isAssessmentResponseReceived, state.noAssessmentsForClass)}
            >
                <DialogTitle id={'no-assessments-title'}>
                    {t('classAssessment.title')}
                </DialogTitle>
                <DialogContent>
                    <Typography id={'no-assessments-description'}>
                        {t('classAssessment.noAssessmentsForClass')}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleConfirmClose}
                        variant={'primary'}
                    >
                        {t('common.close')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                aria-describedby='alert-description'
                aria-labelledby='alert-title'
                onClose={(): void => setState((prev: any) => ({
                    ...prev,
                    showAlert: false
                }))}
                open={state.showAlert}
            >
                <DialogTitle id='alert-title'>
                    {state.alertType === 'success' ? t('classAssessment.submitSuccess') : t('classAssessment.submitFailed')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id='alert-description'>
                        {state.alertMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        autoFocus
                        color='primary'
                        onClick={(): void => {
                            setState((prev: any) => ({
                                ...prev,
                                showAlert: false
                            }));
                            if (state.alertType === 'success') {
                                window.location.reload();
                            }
                        }}
                        type='submit'
                        variant='primary'
                    >
                        {'OK'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
